import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { AlertCircle } from "lucide-react";
import { MONTH_NAMES, currentYearMonth, monthLabel } from "../calendar.utils";
import { cn } from "@/utils/helpers";
import type { BrandCalendarSettings } from "@/types/contentCalendar.type";
import CadenceDialog, { type CadenceDraft } from "../month/CadenceDialog";
import CalendarSettingsModal, { useSettingsMeta, useTakeStrategyScope } from "../components/CalendarSettingsModal";
import CalendarHeader from "../month/CalendarHeader";
import CalendarYearGrid, { monthsForWindow, type YearWindowStart } from "./CalendarYearGrid";
import { useCalendarViewport } from "../month/CalendarSheet";
import { CalendarTopLane } from "../month/PhoneCalendar";
import { calendarState, monthPath, progressOf, type CalendarRow } from "./calendarIndex";
import "../month/calendar.css";

/**
 * The Content calendar index: this brand's year, with the months needing a
 * decision named above it.
 *
 * A "calendar" is one month of this brand's plan, so the page is a year of them
 * and the month view is what you open out of it. It is the front door the
 * Calendar nav item lands on; a planned month opens, and an unplanned one opens
 * a normal conversation where the agent can plan the scoped month itself.
 *
 * THE YEAR LEADS. The page used to be a newest-first list under the shared wall
 * bar (an axis, its chips and a readout), the grammar Trends and References
 * also use. A list is the right shape for a library nobody can hold in their
 * head; twelve months is not that. The year is a fixed, familiar frame, so it
 * is drawn as one and the filter goes away with it: there is nothing to narrow
 * when the whole set is already on screen, and the counts that rode the chips
 * now ride the masthead, where they are read once.
 *
 * Rows are passed in rather than fetched here, so the live route, the walkthrough
 * and the dev harness all render the same component.
 */

export default function CalendarIndexView({
  brandId = null,
  rows,
  settings,
  isLoading = false,
  isError = false,
  onOpenMonth,
  savingCadence = false,
  onSaveCadence,
  pillars,
  clientName = null,
  onWindowChange,
}: {
  /** Told which twelve months are on screen, so the host reads only those. */
  onWindowChange?: (start: YearWindowStart) => void;
  /** The active strategy's pillars, in strategy order, for the posts' hues. */
  pillars?: { id: string; name: string; order?: number }[];
  /** The brand's client, for the whose-turn key. */
  clientName?: string | null;
  /** Null before a brand is picked; the rail needs it to hold a session. */
  brandId?: string | null;
  rows: CalendarRow[];
  /** The brand's cadence: what every month starts from. Edited from the
   *  settings menu here; a single month's own cadence is edited on that month. */
  settings: BrandCalendarSettings | null;
  savingCadence?: boolean;
  /** Saves the brand's cadence. Absent for readers. */
  onSaveCadence?: (draft: CadenceDraft) => Promise<void> | void;
  /** Kept for the hosts that still pass them; the year no longer shows them. */
  pillarCount?: number | null;
  memoryCount?: number | null;
  /** The month list's own read state: an unanswered read must not print twelve
   *  confident "Not planned" months. */
  isLoading?: boolean;
  isError?: boolean;
  /** Given, a planned month calls this instead of navigating to the month route; `plan` starts its plan on open. */
  onOpenMonth?: ((row: CalendarRow, opts?: { plan?: boolean }) => void) | null;
}) {
  const navigate = useNavigate();
  const viewport = useCalendarViewport();
  const desktop = viewport === "desktop";
  // The year is fixed: this calendar year, January to December.
  const start = useMemo<YearWindowStart>(() => ({ year: currentYearMonth().year, month: 1 }), []);
  useEffect(() => {
    onWindowChange?.(start);
  }, [onWindowChange, start]);
  const [editingCadence, setEditingCadence] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const meta = useSettingsMeta(brandId ?? "", editingCadence);
  const takeStrategy = useTakeStrategyScope(brandId ?? "");
  // "Next to start from them: January 2027. December is planned and keeps its 5."
  const reach = useMemo(() => {
    const now = currentYearMonth();
    const ahead = rows
      .filter((row) => row.year * 12 + row.month >= now.year * 12 + now.month)
      .sort((a, b) => a.year * 12 + a.month - (b.year * 12 + b.month));
    const next = ahead.find((row) => !row.planId);
    const planned = ahead.filter((row) => row.planId && row.targets);
    if (!next) return null;
    const nextLabel = monthLabel(next);
    const kept =
      planned.length === 1 && planned[0].targets
        ? ` ${MONTH_NAMES[planned[0].month - 1]} is planned and keeps its ${
            planned[0].targets.static + planned[0].targets.vertical + planned[0].targets.horizontal
          }.`
        : planned.length > 1
          ? " Months already planned keep their numbers."
          : "";
    return `Next to start from them: ${nextLabel}.${kept}`;
  }, [rows]);

  /*
    Plan {Month} on a tile (Figma 08, 13 Oct): the month opens and its plan starts
    at once; Creo's two questions wait in the side chat. The month view reads
    `plan=1` once and starts the run (CalendarExperienceView).
  */
  const startPlanning = useCallback(
    (row: CalendarRow) => {
      if (onOpenMonth) onOpenMonth(row, { plan: true });
      else navigate(`${monthPath(row)}&plan=1`);
    },
    [navigate, onOpenMonth],
  );

  const windowMonths = useMemo(() => monthsForWindow(start, rows), [start, rows]);
  const running = windowMonths.filter((row) => calendarState(row) === "running").length;
  const needYou = windowMonths.reduce((sum, row) => sum + (progressOf(row)?.needsYou ?? 0), 0);
  const status = isError
    ? "Could not read this brand's calendars."
    : isLoading
      ? "Reading this brand's calendars…"
      : rows.length === 0
        ? "No calendars yet."
        : `${running} ${running === 1 ? "month is" : "months are"} in progress${needYou > 0 ? `; ${needYou} ${needYou === 1 ? "post needs" : "posts need"} you` : ""}.`;

  const title = String(start.year);
  const openRow = (year: number, month: number) => {
    const row = windowMonths.find((r) => r.year === year && r.month === month) ?? rows.find((r) => r.year === year && r.month === month);
    if (row && row.planStatus) {
      if (onOpenMonth) onOpenMonth(row);
      else navigate(monthPath(row));
    } else navigate(monthPath({ year, month } as CalendarRow));
  };

  return (
    <div className={cn("relative flex min-w-0 flex-col", desktop ? "co-cal-page" : "min-h-[calc(100dvh-66px)] w-full pb-10")}>
      {desktop ? null : <CalendarTopLane brandName={clientName} />}
      <CalendarHeader
        layout={desktop ? "desktop" : "tablet"}
        views={false}
        dense
        title={title}
        compactTitle={title}
        view="month"
        onViewChange={() => undefined}
        arrows={false}
        onPrev={() => undefined}
        onNext={() => undefined}
        prevLabel="Earlier months"
        nextLabel="Later months"
        picker={{
          year: start.year,
          month: start.month,
          markOpen: false,
          monthsWithPosts: new Set(
            rows.filter((row) => (progressOf(row)?.total ?? 0) > 0).map((row) => `${row.year}-${String(row.month).padStart(2, "0")}`),
          ),
          onPick: openRow,
        }}
        status={status}
        statusTone={isError ? "alert" : isLoading ? "muted" : "default"}
        settings={
          desktop && settings && onSaveCadence ? [{ label: "Posts a month", onPress: () => setEditingCadence(true) }] : []
        }
      />

      {isError ? (
        <div className={cn("flex items-start gap-3 rounded-card bg-co-surface px-4 py-6", desktop && "mx-8")}>
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-co-alert-ink" aria-hidden="true" />
          <p className="text-[13px] leading-relaxed text-co-muted">
            Something went wrong fetching this brand&apos;s calendars, so the year cannot be drawn. Try refreshing in a
            moment.
          </p>
        </div>
      ) : isLoading ? (
        <p className={cn("py-10 text-[13px] text-co-faint", desktop && "px-8")}>Reading this brand&apos;s calendars…</p>
      ) : (
        <CalendarYearGrid
          start={start}
          rows={rows}
          settings={settings}
          onOpenMonth={onOpenMonth}
          onPlan={startPlanning}
          pillars={pillars}
          clientName={clientName}
        />
      )}

      {editingCadence && settings && onSaveCadence ? (
        // Figma 08, "Posts a month, from the year page".
        <CadenceDialog
          scope="brand"
          title="Posts a month"
          description={`${clientName?.trim() ? `${clientName.trim()}’s` : "The brand’s"} numbers for every month Creo plans from now on.`}
          initial={settings}
          strategy={meta.data?.strategyScope ?? null}
          onUseStrategy={
            brandId
              ? () => {
                  setEditingCadence(false);
                  takeStrategy.mutate();
                }
              : undefined
          }
          reach={reach}
          onOpenSettings={
            brandId
              ? () => {
                  setEditingCadence(false);
                  setSettingsOpen(true);
                }
              : undefined
          }
          saving={savingCadence || takeStrategy.isPending}
          onSave={async (draft) => {
            try {
              await onSaveCadence(draft);
              setEditingCadence(false);
            } catch {
              // The mutation owns the error toast. Keep the draft in place.
            }
          }}
          onClose={() => setEditingCadence(false)}
        />
      ) : null}

      {settingsOpen && brandId && settings ? (
        <CalendarSettingsModal brandId={brandId} settings={settings} brandName={clientName} onClose={() => setSettingsOpen(false)} />
      ) : null}
    </div>
  );
}
